/**
 * The pure half of the «Данные УНТИ» page (T.U2) — the same split every page
 * keeps: what the URL means, what the api is asked, and how a mirror row
 * renders. Dependency-free except TYPE imports (erased at runtime), so the
 * plain-Node `unit` Playwright project can pin every fold without a DOM — the
 * rule `../applications/applications-model.ts` documents.
 *
 * The page is a RAW MIRROR view (owner brief): every field is THEIR value
 * verbatim, every vocabulary is theirs. Nothing here translates, casts or
 * normalises a mirror value — «как в API» is the feature.
 */

import { EVI_FAILED_STATUS_TOKEN, displayStatusOf, type AppStatus } from '@cb-manager/shared';

import type { UntiApplicationDto, UntiApplicationListQuery, UntiApplicationOurDataDto } from '../../api/unti';
// Type-only: the settings panel owns the value-import of `client.ts`
// (`import.meta.env`), which the plain-Node unit project cannot load.
import type { Unti2035StatusDto } from '../../api/system-status';
// The register's OWN vocabulary and folds — the «наш статус»/«Договор»/«Формат»
// columns must read exactly like the register's (T.Z147's brief). Every import
// here is a value import of a framework-free module (the same rule
// `applications-model.ts` documents for itself), so the plain-Node unit project
// keeps running.
import {
  CONTRACT_FILTER_OPTIONS,
  CONTRACT_STATE_SHORT,
  CONTRACT_STATE_TONE,
  FORMAT_LABEL,
  TRANSFERT_FILTER_NONE,
  TRANSFERT_NONE_LABEL,
  displayStatusView,
  parseTransfertParam,
  transfertFilterOptions,
} from '../applications/applications-model';
import { STATUS_LABEL, type BadgeTone } from '../dashboard/status';

// ---------------------------------------------------------------------------
// URL state
// ---------------------------------------------------------------------------

/** Query params this page owns (`lib/query-params` merges them into the URL). */
export const PARAM = {
  /** Their course id = our programme id (one id space, probed live). */
  COURSE_ID: 'courseId',
  /** RAW status — their vocabulary, sent verbatim. */
  STATUS: 'status',
  /** Exact unti_id. */
  UNTI_ID: 'untiId',
  /** Substring over unti_id / pgu_id — #36's `search`. */
  SEARCH: 'search',
  /**
   * ADDITIVE (v1.9.42, T.Z153) — the «Итоговое испытание» status, #36's
   * `exitExamStatus` (the mirror column `cof_exit_exam_status`, ONE value or a
   * comma list of the five raw literals).
   */
  EXIT_EXAM: 'exitExamStatus',
  /**
   * ADDITIVE (v1.9.88, T.Z238) — the «Трансферт» filter, #36's `transfert`: the
   * RAW mirror row's OWN `transfert_status` word, verbatim, or the api's `none`
   * literal for the NULL/'' bucket. The register's own param name and grammar
   * (`applications-model.ts` PARAM.TRANSFERT) — one word, one spelling, two
   * pages; the vocabularies differ by design (this one is the mirror-column
   * set, #36.1's answer, not the winner fold).
   */
  TRANSFERT: 'transfert',
  /** ADDITIVE (v1.9.40, T.Z147) — OUR display status (the #3 status grammar). */
  OUR_STATUS: 'ourStatus',
  /** ADDITIVE (v1.9.40, T.Z147) — OUR «Формат обучения». */
  FORMAT: 'format',
  /** ADDITIVE (v1.9.40, T.Z147) — the contract-fact bucket. */
  CONTRACT: 'contract',
  /** ADDITIVE (v1.9.40, T.Z147) — the «Провалил ЕВИ» fold. */
  EVI_FAILED: 'eviFailed',
  /** ADDITIVE (v1.9.40, T.Z147) — the join state (`false` = «не найдено»). */
  LINKED: 'linked',
  PAGE: 'page',
  PAGE_SIZE: 'pageSize',
  /** Detail drawer: `/unti?id=<surrogate id>` — #37's path parameter. */
  ID: 'id',
  /**
   * The sort state (`sortBy` + `order`) — see `SortState`. Since v1.9.43
   * (T.Z154) it drives #36's server-side sort: `filtersToQuery` maps the pair
   * onto the api's `sort`/`dir`, and the default state sends nothing, so the
   * api's standing `applied_at`-DESC ordering answers an unsorted view.
   */
  SORT_BY: 'sortBy',
  ORDER: 'order',
} as const;

export const DEFAULT_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS: ReadonlyArray<number> = [10, 20, 50, 100];

// ---------------------------------------------------------------------------
// «Итоговое испытание» (ADDITIVE v1.9.42, T.Z153) — the owner's dictionary of
// the mirror column `cof_exit_exam_status`
// ---------------------------------------------------------------------------

/**
 * The column title the owner asked for. The same mirror field is captioned
 * «Выходной экзамен — статус» in the detail drawer (the integration plan's own
 * field walk) and «Итоговое испытание» here — the owner's business name for it;
 * the drawer keeps its per-field captions, the table speaks the owner's word.
 */
export const EXIT_EXAM_COLUMN = 'Итоговое испытание';

/** The five raw literals their API documents (#36's whole filter vocabulary). */
export type ExitExamStatusValue = 'start' | 'InProcess' | 'Success' | 'UnSuccess' | 'NoStartExpired';

export const EXIT_EXAM_STATUS_VALUES: ReadonlyArray<ExitExamStatusValue> = [
  'start',
  'InProcess',
  'Success',
  'UnSuccess',
  'NoStartExpired',
];

/**
 * THE owner's dictionary, verbatim — the RU text of each raw value. Store
 * AS-IS: the mirror keeps their string, this map is the VIEW layer's only
 * translation (nothing here writes back, and the api answers the raw values).
 */
export const EXIT_EXAM_STATUS_LABEL: Readonly<Record<ExitExamStatusValue, string>> = {
  start: 'Зашёл на страницу итогового испытания',
  InProcess: 'Приступил к итоговому испытанию',
  Success: 'Успешное прохождение итогового испытания',
  UnSuccess: 'Неуспешное прохождение итогового испытания',
  NoStartExpired: 'Не приступил к прохождению итогового испытания в установленный срок',
};

/**
 * The compact forms the TABLE prints — the five full sentences would not fit a
 * cell (or a select). The full text rides the cell's `title`, next to the raw
 * code, so nothing is lost.
 */
export const EXIT_EXAM_STATUS_SHORT: Readonly<Record<ExitExamStatusValue, string>> = {
  start: 'Зашёл на страницу',
  InProcess: 'Приступил',
  Success: 'Успешно',
  UnSuccess: 'Неуспешно',
  NoStartExpired: 'Не приступил, срок истёк',
};

export interface ExitExamStatusView {
  /** The cell text: the compact label — or the raw value kept honest. */
  label: string;
  /** The full dictionary text + the raw code (or the drift, for an unknown one). */
  title: string;
  /** False = a value outside the five: the cell reads «—», the title keeps it. */
  known: boolean;
}

/**
 * The cell fold of one mirror value. `null`/`''` → `null` (the plain «—»).
 * A KNOWN value → the compact label with the full dictionary text and the raw
 * code in the title (the raw value stays visible to the ADMIN without a chip).
 * An UNKNOWN value → the owner's «—» as the cell text, the raw spelling in the
 * title — their vocabulary may drift, and a drift is data, never an error.
 */
export function exitExamStatusView(value: string | null | undefined): ExitExamStatusView | null {
  if (value === null || value === undefined || value === '') return null;
  if ((EXIT_EXAM_STATUS_VALUES as readonly string[]).includes(value)) {
    const known = value as ExitExamStatusValue;
    return { label: EXIT_EXAM_STATUS_SHORT[known], title: `${EXIT_EXAM_STATUS_LABEL[known]} (код API: ${value})`, known: true };
  }
  return { label: '—', title: `Неизвестный для нас код — как отдал API: ${value}`, known: false };
}

/**
 * `?exitExamStatus=` — ONE value or a comma list, matched VERBATIM (the
 * dictionary is mixed-case: `success` is not `Success`). Junk tokens are
 * DROPPED — the api would 400 them, and they never leave the browser (the
 * register's rule).
 */
export function parseExitExamStatusParam(value: string | undefined): ExitExamStatusValue[] | undefined {
  if (typeof value !== 'string') return undefined;
  const values: ExitExamStatusValue[] = [];
  for (const token of value.split(',')) {
    const candidate = token.trim();
    if (!(EXIT_EXAM_STATUS_VALUES as readonly string[]).includes(candidate)) continue;
    if (!values.includes(candidate as ExitExamStatusValue)) values.push(candidate as ExitExamStatusValue);
  }
  return values.length > 0 ? values : undefined;
}

/** The «Итоговое испытание» select: «Все», the five compact labels — plus one injected preset option for a multi-value link. */
export function exitExamFilterOptions(rawStatus: string | undefined): Array<{ value: string; label: string }> {
  const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Все' }];
  for (const value of EXIT_EXAM_STATUS_VALUES) {
    options.push({ value, label: EXIT_EXAM_STATUS_SHORT[value] });
  }
  const raw = typeof rawStatus === 'string' ? rawStatus.trim() : '';
  if (raw === '' || (EXIT_EXAM_STATUS_VALUES as readonly string[]).includes(raw)) return options;
  const parsed = parseExitExamStatusParam(raw);
  const label = parsed !== undefined ? parsed.map((value) => EXIT_EXAM_STATUS_SHORT[value]).join(' + ') : raw;
  options.push({ value: raw, label });
  return options;
}

// ---------------------------------------------------------------------------
// «Трансферт» (ADDITIVE v1.9.88, T.Z238) — the mirror column `transfert_status`
// ---------------------------------------------------------------------------

/**
 * The «no value» literal and the select's label for it — the REGISTER's own
 * grammar (v1.9.87, T.Z237), imported, never re-spelled: one word, one label,
 * two pages. Here the bucket is the row's OWN NULL/'' column (the register's
 * folds the leaf-winner in on top), and the vocabulary is the mirror-column set
 * the #36.1 facet answers — a strict superset of the register's winner fold.
 */
export { TRANSFERT_FILTER_NONE, TRANSFERT_NONE_LABEL, transfertFilterOptions };

/**
 * The «Трансферт» select's option build over the #36.1 facet's answer:
 * «Все» and «Не указан» ride ahead of the facet's words (the register's bar
 * builds the same shape), a value the URL carries that the facet does not know
 * stays selectable as itself — a hand-edited link must never read as «nothing
 * selected» (the venue/program-short rescue; the api answers an unknown word
 * with its own 400, which is the honest refusal).
 */
export function untiTransfertSelectOptions(
  values: ReadonlyArray<string>,
  rawValue: string | undefined,
): Array<{ value: string; label: string }> {
  return transfertFilterOptions(values, rawValue);
}

export interface UntiSortField {
  /** The sort key the column speaks — also the `?sortBy=` value. */
  id: SortableColumn;
  label: string;
  num?: boolean;
}

/**
 * ADDITIVE (v1.9.43, T.Z154) — the columns a header click orders. Since the
 * api grew `?sort=&dir=`, a click is a SERVER sort over the WHOLE filtered
 * set: the click writes `?sortBy=<key>&order=asc|desc` into the URL, the query
 * maps it onto #36's `sort`/`dir`, and the api returns the reordered slice —
 * the page never reorders rows itself (no client-side sort is left here). The
 * twelve keys are exactly the api's whitelist: eight MIRROR columns (the
 * `UntiApplicationDto` fields) and the four OUR-join columns, whose values
 * live in the row's `our` block. The «Наши данные» chip column has no value of
 * its own to order by — it stays a plain head, its state is `?linked=`'s job.
 */
export type SortableColumn =
  | 'untiId'
  | 'courseId'
  | 'status'
  | 'transfertStatus'
  | 'appliedAt'
  | 'endDt'
  | 'untillDt'
  | 'cofExitExamStatus'
  | 'ourStatus'
  | 'format'
  | 'contract'
  | 'eviFailed';

export const SORTABLE_COLUMNS: ReadonlyArray<UntiSortField> = [
  { id: 'untiId', label: 'УНТИ ID' },
  { id: 'courseId', label: 'Курс', num: true },
  { id: 'status', label: 'Статус' },
  { id: 'ourStatus', label: 'Наш статус' },
  { id: 'format', label: 'Формат' },
  { id: 'contract', label: 'Договор' },
  { id: 'eviFailed', label: 'Провалил ЕВИ' },
  { id: 'transfertStatus', label: 'Transfert' },
  { id: 'appliedAt', label: 'Подано' },
  { id: 'endDt', label: 'Окончание' },
  { id: 'untillDt', label: 'Автооткл.' },
  { id: 'cofExitExamStatus', label: EXIT_EXAM_COLUMN },
];

/** The header tooltip since v1.9.43 — the api orders the data, not the page. */
export const SERVER_SORT_HINT =
  'Сортировка по всем записям выборки — порядок считает api, а не эта страница';

/**
 * ADDITIVE (v1.9.40, T.Z147) — the header row as the table renders it. The
 * plain heads carry a `testId` the header/skeleton render.
 *
 * ADDITIVE (T.Z153) — `our: true` marks the four columns that read OUR join
 * (`applications a`), not the mirror: the visual twin of the «Фильтр по данным
 * cb-manager» group, rendered as a gray tint (`td.col-our` in unti.css).
 *
 * ADDITIVE (v1.9.43, T.Z154) — the four our columns are SORTABLE now (their
 * keys ride #36's `?sort=` whitelist over the joined columns), so they carry
 * `id`s and render the same header button the mirror columns do; only the
 * «Наши данные» chip column stays plain.
 */
export interface UntiHeadSpec {
  id?: SortableColumn;
  label: string;
  testId?: string;
  num?: boolean;
  /** This column reads OUR joined application, not the mirror row. */
  our?: boolean;
}

export const TABLE_HEADS: ReadonlyArray<UntiHeadSpec> = [
  { id: 'untiId', label: 'УНТИ ID' },
  { id: 'courseId', label: 'Курс', num: true },
  { id: 'status', label: 'Статус' },
  { id: 'ourStatus', label: 'Наш статус', testId: 'head-our-status', our: true },
  { id: 'format', label: 'Формат', testId: 'head-our-format', our: true },
  { id: 'contract', label: 'Договор', testId: 'head-our-contract', our: true },
  { id: 'eviFailed', label: 'Провалил ЕВИ', testId: 'head-our-evi', our: true },
  { id: 'transfertStatus', label: 'Transfert' },
  { id: 'appliedAt', label: 'Подано' },
  { id: 'endDt', label: 'Окончание' },
  { id: 'untillDt', label: 'Автооткл.' },
  { id: 'cofExitExamStatus', label: EXIT_EXAM_COLUMN },
  { label: 'Наши данные', testId: 'head-our-card' },
];

export type SortOrder = 'asc' | 'desc';
export interface SortState {
  field: SortableColumn;
  order: SortOrder;
}

/** The api's own ordering, spelled as a sort state: fresh applications first. */
export const DEFAULT_SORT: SortState = { field: 'appliedAt', order: 'desc' };

export function isDefaultSort(sort: SortState): boolean {
  return sort.field === DEFAULT_SORT.field && sort.order === DEFAULT_SORT.order;
}

/** Positive integer or `undefined` (a junk value is a URL typo, not an api error). */
export function parseIntParam(value: string | undefined): number | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function trimParam(value: string | undefined): string | undefined {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed === '' ? undefined : trimmed;
}

// ---------------------------------------------------------------------------
// The our-half filters (ADDITIVE v1.9.40, T.Z147) — the register's own grammar
// ---------------------------------------------------------------------------

/** The six frozen statuses plus the VIRTUAL token, exactly what #36 accepts. */
export type OurStatusFilterValue = AppStatus | typeof EVI_FAILED_STATUS_TOKEN;

export const OUR_STATUS_FILTER_VALUES: ReadonlyArray<OurStatusFilterValue> = [
  'WAITING_LINK',
  'LINK_RECEIVED',
  'EVI_PASSED',
  'REJECTED',
  'AWAITING_INFO',
  'NO_DATA',
  EVI_FAILED_STATUS_TOKEN,
];

/** THE RU vocabulary of the register — one map, reused, never re-spelled here. */
export const OUR_STATUS_FILTER_LABEL: Readonly<Record<OurStatusFilterValue, string>> = {
  ...STATUS_LABEL,
  [EVI_FAILED_STATUS_TOKEN]: 'Провалил ЕВИ',
};

export type OurFormatFilter = 'ONLINE' | 'OFFLINE';

/** The contract buckets #36 answers — #3's lowercase literals minus the enrollment pair. */
export type OurContractFilter = 'signed' | 'not_signed' | 'not_arrived';

export const OUR_CONTRACT_FILTERS: ReadonlyArray<OurContractFilter> = ['signed', 'not_signed', 'not_arrived'];

/** The «Договор» options: the register's own labels, cut to the three buckets. */
export const OUR_CONTRACT_OPTIONS: ReadonlyArray<{ value: OurContractFilter; label: string }> =
  CONTRACT_FILTER_OPTIONS.filter((option): option is { value: OurContractFilter; label: string } =>
    (OUR_CONTRACT_FILTERS as readonly string[]).includes(option.value),
  );

/** `?ourStatus=` — ONE token or a comma list; junk tokens are DROPPED (the register's rule), no valid token at all ⇒ no filter. */
export function parseOurStatusParam(value: string | undefined): OurStatusFilterValue[] | undefined {
  if (typeof value !== 'string') return undefined;
  const values: OurStatusFilterValue[] = [];
  for (const token of value.split(',')) {
    const candidate = token.trim().toUpperCase();
    if (!OUR_STATUS_FILTER_VALUES.includes(candidate as OurStatusFilterValue)) continue;
    if (!values.includes(candidate as OurStatusFilterValue)) values.push(candidate as OurStatusFilterValue);
  }
  return values.length > 0 ? values : undefined;
}

/** The «Наш статус» select: «Все», the seven singles — plus one injected preset option for a multi-value link. */
export function ourStatusFilterOptions(rawStatus: string | undefined): Array<{ value: string; label: string }> {
  const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Все' }];
  for (const status of OUR_STATUS_FILTER_VALUES) {
    options.push({ value: status, label: OUR_STATUS_FILTER_LABEL[status] });
  }
  const raw = typeof rawStatus === 'string' ? rawStatus.trim() : '';
  if (raw === '' || OUR_STATUS_FILTER_VALUES.includes(raw as OurStatusFilterValue)) return options;
  const parsed = parseOurStatusParam(raw);
  const label = parsed !== undefined ? parsed.map((status) => OUR_STATUS_FILTER_LABEL[status]).join(' + ') : raw;
  options.push({ value: raw, label });
  return options;
}

export interface UntiFilters {
  courseId?: number;
  status?: string;
  untiId?: string;
  search?: string;
  /** ADDITIVE (v1.9.42, T.Z153) — the «Итоговое испытание» status, verbatim raw values. */
  exitExamStatus?: ExitExamStatusValue[];
  /**
   * ADDITIVE (v1.9.88, T.Z238) — the «Трансферт» word of the RAW mirror row:
   * ONE value verbatim or the `none` literal. The vocabulary is the api's to
   * judge (the #36.1 facet offers it), so the browser passes the trimmed value
   * through — a hand-edited junk word RIDES (the `programShort` reading: the
   * api answers it with its own 400, never a silently empty page).
   */
  transfert?: string;
  /** ADDITIVE (v1.9.40, T.Z147) — the our-half filters, parsed and validated here. */
  ourStatus?: OurStatusFilterValue[];
  format?: OurFormatFilter;
  contract?: OurContractFilter;
  eviFailed?: boolean;
  linked?: boolean;
  page: number;
  pageSize: number;
  sort: SortState;
}

export const DEFAULT_FILTERS: UntiFilters = { page: 1, pageSize: DEFAULT_PAGE_SIZE, sort: DEFAULT_SORT };

function parseBoolParam(value: string | undefined): boolean | undefined {
  const trimmed = trimParam(value);
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  return undefined;
}

/** URL params → filters. Anything the api would reject never leaves the browser. */
export function parseFilters(params: Record<string, string>): UntiFilters {
  const page = parseIntParam(params[PARAM.PAGE]) ?? 1;
  const pageSize = parseIntParam(params[PARAM.PAGE_SIZE]) ?? DEFAULT_PAGE_SIZE;
  const format = trimParam(params[PARAM.FORMAT]);
  const contract = trimParam(params[PARAM.CONTRACT]);
  return {
    courseId: parseIntParam(params[PARAM.COURSE_ID]),
    status: trimParam(params[PARAM.STATUS]),
    untiId: trimParam(params[PARAM.UNTI_ID]),
    search: trimParam(params[PARAM.SEARCH]),
    exitExamStatus: parseExitExamStatusParam(params[PARAM.EXIT_EXAM]),
    transfert: parseTransfertParam(params[PARAM.TRANSFERT]),
    ourStatus: parseOurStatusParam(params[PARAM.OUR_STATUS]),
    format: format === 'ONLINE' || format === 'OFFLINE' ? format : undefined,
    contract: (OUR_CONTRACT_FILTERS as readonly string[]).includes(contract ?? '')
      ? (contract as OurContractFilter)
      : undefined,
    eviFailed: parseBoolParam(params[PARAM.EVI_FAILED]),
    linked: parseBoolParam(params[PARAM.LINKED]),
    page: Math.max(page, 1),
    pageSize,
    sort: parseSortState(params),
  };
}

/** The api query of #36 — defaults dropped, so an unfiltered view sends `?`-nothing. */
export function filtersToQuery(filters: UntiFilters): UntiApplicationListQuery {
  const query: UntiApplicationListQuery = {};
  if (filters.courseId !== undefined) query.courseId = filters.courseId;
  if (filters.status) query.status = filters.status;
  if (filters.untiId) query.untiId = filters.untiId;
  if (filters.search) query.search = filters.search;
  if (filters.exitExamStatus !== undefined) query.exitExamStatus = filters.exitExamStatus.join(',');
  // ADDITIVE (v1.9.88, T.Z238) — the «Трансферт» word verbatim (`none` included;
  // the register's parse already trimmed and dropped the empty value).
  if (filters.transfert) query.transfert = filters.transfert;
  if (filters.ourStatus !== undefined) query.ourStatus = filters.ourStatus.join(',');
  if (filters.format !== undefined) query.format = filters.format;
  if (filters.contract !== undefined) query.contract = filters.contract;
  if (filters.eviFailed !== undefined) query.eviFailed = filters.eviFailed;
  if (filters.linked !== undefined) query.linked = filters.linked;
  // ADDITIVE (v1.9.43, T.Z154) — the sort rides #36's own `sort`/`dir`: the
  // DEFAULT state sends NEITHER, so the api answers its standing
  // `applied_at`-DESC ordering byte for byte, and a click asks the server to
  // reorder the WHOLE filtered set (the page never sorts rows itself).
  if (!isDefaultSort(filters.sort)) {
    query.sort = filters.sort.field;
    query.dir = filters.sort.order;
  }
  if (filters.page > 1) query.page = filters.page;
  if (filters.pageSize !== DEFAULT_PAGE_SIZE) query.pageSize = filters.pageSize;
  return query;
}

/** True when ANY of the our-half filters carries a value (the reset button, the empty state). */
export function hasOurFilters(filters: UntiFilters): boolean {
  return (
    filters.ourStatus !== undefined ||
    filters.format !== undefined ||
    filters.contract !== undefined ||
    filters.eviFailed !== undefined ||
    filters.linked !== undefined
  );
}

// ---------------------------------------------------------------------------
// The two named filter groups (T.Z153) — what reads the MIRROR vs what reads
// OUR join
// ---------------------------------------------------------------------------

export interface UntiFilterGroup {
  key: 'unti' | 'our';
  /** The heading the group renders. */
  title: string;
  /** The heading's tooltip — which table each group's conditions read. */
  hint: string;
  /** The controls of the group, in render order, by their testId. */
  controls: ReadonlyArray<string>;
}

/**
 * T.Z153 — the page's filters split into two labelled blocks, by the TABLE each
 * condition reads:
 *
 *   * «Фильтр по данным УНТИ» — everything over the MIRROR `unti_applications`
 *     (курс, RAW статус УНТИ, «Трансферт» since v1.9.88/T.Z238, unti_id/поиск,
 *     and the «Итоговое испытание» of v1.9.42), the data cat.2035.university
 *     gave us;
 *   * «Фильтр по данным cb-manager» — everything over the JOINED OUR
 *     application (наш статус, Формат обучения, Договор, «Провалил ЕВИ», and
 *     the join state «Наши данные»/«Не найдено в наших данных»).
 *
 * A LAYOUT fact only: the request semantics of every filter are untouched, the
 * api still answers one #36 read. The table's `our: true` head columns are the
 * visual twin of the second group. The page renders the groups in this array's
 * order; the e2e suite pins the DOM against this very declaration.
 */
export const UNTI_FILTER_GROUPS: ReadonlyArray<UntiFilterGroup> = [
  {
    key: 'unti',
    title: 'Фильтр по данным УНТИ',
    hint: 'Условия по зеркалу unti_applications — как их отдал cat.2035.university',
    controls: ['filter-course', 'filter-status', 'filter-transfert', 'filter-exit-exam', 'filter-unti-id', 'filter-search'],
  },
  {
    key: 'our',
    title: 'Фильтр по данным cb-manager',
    hint: 'Условия по НАШЕЙ заявке, найденной по unti_id (join applications)',
    controls: ['filter-our-status', 'filter-our-format', 'filter-our-contract', 'filter-our-evi', 'filter-linked'],
  },
];

export function pageCount(total: number, pageSize: number): number {
  if (pageSize <= 0) return 1;
  return Math.max(Math.ceil(total / pageSize), 1);
}

/** The api clamps a page that ran past the end; the URL follows on the next render. */
export function clampPage(page: number, total: number, pageSize: number): number {
  return Math.min(page, pageCount(total, pageSize));
}

// ---------------------------------------------------------------------------
// The sort state (ADDITIVE v1.9.43, T.Z154 — a SERVER sort, see SortableColumn)
// ---------------------------------------------------------------------------

/** `?sortBy=<key>&order=asc|desc`; a junk pair falls back to the api's ordering. */
export function parseSortState(params: Record<string, string>): SortState {
  const field = params[PARAM.SORT_BY]?.trim() as SortableColumn | undefined;
  const order = params[PARAM.ORDER]?.trim();
  if (field && SORTABLE_COLUMNS.some((column) => column.id === field)) {
    return { field, order: order === 'asc' ? 'asc' : order === 'desc' ? 'desc' : 'asc' };
  }
  return DEFAULT_SORT;
}

/** A sorted state's URL patch; the default state empties BOTH keys. */
export function formatSortState(sort: SortState): Record<string, string | undefined> {
  if (isDefaultSort(sort)) return { [PARAM.SORT_BY]: undefined, [PARAM.ORDER]: undefined };
  return { [PARAM.SORT_BY]: sort.field, [PARAM.ORDER]: sort.order };
}

/** The first click on a column starts ascending; the next click on it flips. */
export function toggleSort(current: SortState, field: SortableColumn): SortState {
  if (current.field === field) return { field, order: current.order === 'asc' ? 'desc' : 'asc' };
  return { field, order: 'asc' };
}

export function sortArrow(column: SortableColumn, sort: SortState): SortOrder | null {
  return sort.field === column ? sort.order : null;
}

// ---------------------------------------------------------------------------
// The status vocabulary (theirs, store-as-is)
// ---------------------------------------------------------------------------

/** The statuses THEIR side documents or has shown live (v1.9.38 contract note). */
export const UNTI_STATUS_KNOWN: ReadonlyArray<string> = ['approved', 'accepted', 'declined', 'review'];

/**
 * The «Статус» select's option list: the known vocabulary FIRST, then whatever
 * the loaded page actually shows that the list does not know yet — a drift on
 * their side surfaces as a new option built from real data, never as an
 * untranslatable row. A URL value the list has never seen is kept too, so a
 * shared link renders as the SELECTED preset it is.
 */
export function statusFilterOptions(observed: ReadonlyArray<string>, selected?: string): Array<{ value: string; label: string }> {
  const extra: string[] = [];
  const seen = new Set(UNTI_STATUS_KNOWN);
  for (const value of observed) {
    if (value !== '' && !seen.has(value)) {
      seen.add(value);
      extra.push(value);
    }
  }
  if (selected && !seen.has(selected)) extra.unshift(selected);
  return [
    { value: '', label: 'Все' },
    ...[...UNTI_STATUS_KNOWN, ...extra].map((value) => ({ value, label: value })),
  ];
}

/** The raw status pill's tone — the four known values get their colour, an
 *  unknown value reads neutral (a drift is data, not an error). */
export function statusTone(status: string): 'ok' | 'bad' | 'warn' | 'neutral' {
  switch (status) {
    case 'approved':
    case 'accepted':
      return 'ok';
    case 'declined':
      return 'bad';
    case 'review':
      return 'warn';
    default:
      return 'neutral';
  }
}

// ---------------------------------------------------------------------------
// The our-half display folds (ADDITIVE v1.9.40, T.Z147) — the register's own
// rendering, one `our` block away
// ---------------------------------------------------------------------------

/**
 * The «Наш статус» cell: the register's own badge (the VIRTUAL «Провалил ЕВИ»
 * when the fact holds, the stored status otherwise). `null` = the row is not
 * in our data — the cell renders the quiet «—», never a guessed status.
 */
export function ourStatusView(
  our: UntiApplicationOurDataDto | null | undefined,
): { label: string; tone: BadgeTone; title: string } | null {
  if (!our) return null;
  const view = displayStatusView({ status: our.ourStatus, eviFailed: our.eviFailed });
  return { label: view.label, tone: view.tone, title: view.title };
}

/** The fold the badge read — shared for the column and the child card. */
export function ourDisplayStatus(
  our: UntiApplicationOurDataDto | null | undefined,
): AppStatus | typeof EVI_FAILED_STATUS_TOKEN | null {
  if (!our) return null;
  // No clock on purpose (T.Z204): this page keeps the v1.9.40 vocabulary — the six
  // stored statuses + `EVI_FAILED` — and never speaks the register's «Не прошёл ЕВИ»
  // deadline token (the contract documents the difference). The fold without `now`
  // cannot return it; the assertion only narrows the wider `DisplayStatus` type.
  return displayStatusOf({ status: our.ourStatus, eviFailed: our.eviFailed }) as
    | AppStatus
    | typeof EVI_FAILED_STATUS_TOKEN;
}

/** The «Договор» cell: the register's short labels and ink; `null` = «—». */
export function ourContractView(
  our: UntiApplicationOurDataDto | null | undefined,
): { label: string; tone: BadgeTone } | null {
  if (!our || our.contractState === null) return null;
  return { label: CONTRACT_STATE_SHORT[our.contractState], tone: CONTRACT_STATE_TONE[our.contractState] };
}

/** The «Формат» cell text: «Офлайн»/«Онлайн» or the absence marker. */
export function ourFormatLabel(our: UntiApplicationOurDataDto | null | undefined): string | null {
  if (!our || our.format === null) return null;
  return FORMAT_LABEL[our.format];
}

/** The «Провалил ЕВИ» cell: the fact or nothing (one fold, both sources). */
export function ourEviFailedLabel(our: UntiApplicationOurDataDto | null | undefined): string | null {
  if (!our || !our.eviFailed) return null;
  return 'Провалил ЕВИ';
}

// ---------------------------------------------------------------------------
// The top widgets — numbers first (owner brief), folded off #23's `unti2035`
// ---------------------------------------------------------------------------

export interface UntiWidget {
  testId: string;
  label: string;
  /** The number (or the freshness caption) as the card prints it. */
  value: string;
  /** The card's `title` — where the number comes from. */
  hint: string;
  tone: 'ok' | 'bad' | 'warn' | 'neutral';
  /** A widget deep-links into the table's filtered state when one exists. */
  href?: string;
}

/**
 * The four cards. «Заявлений в зеркале» and «Отказов по УНТИ» are the two LIVE
 * counts of `#23.unti2035` (`mirrorTotal` / `applicationsDeclined`); «Синк
 * (API)» is the sweep's freshness (`state` + «данные на …» off `lastRunAt`).
 * ADDITIVE (v1.9.40, T.Z147) — «Не найдено в наших данных» is #36's own
 * `meta.notFoundInOurData` (NOT a `#23` number): the anti-join over the whole
 * mirror, and its click sends `?linked=false`.
 *
 * The counts deep-link where a TABLE state exists: «Заявлений в зеркале» is
 * exactly the unfiltered table, so it links to `/unti` (the clean view).
 * «Отказов по УНТИ» counts OUR fact (`applications.unti_declined_at IS NOT
 * NULL`) — #3 has no parameter for it (checked against the api), so the card
 * has NO honest deep link and carries the number alone; the mirror's own raw
 * `declined` STATUS is a different fact and gets its own filter in the table.
 */
export function untiWidgets(sync: Unti2035StatusDto | undefined, notFoundInOurData?: number | null): UntiWidget[] {
  const mirrorTotal = sync?.mirrorTotal;
  const declined = sync?.applicationsDeclined;
  return [
    {
      testId: 'unti-widget-mirror',
      label: 'Заявлений в зеркале',
      value: typeof mirrorTotal === 'number' ? formatCount(mirrorTotal) : '—',
      hint: 'Строк в кэше УНТИ (unti_applications) после последнего синка',
      tone: 'neutral',
      href: '/unti',
    },
    {
      testId: 'unti-widget-unlinked',
      label: 'Не найдено в наших данных',
      value: typeof notFoundInOurData === 'number' ? formatCount(notFoundInOurData) : '—',
      hint: 'Строк зеркала, чей unti_id нет ни в одном нашем заявлении — нажмите, чтобы показать только их',
      tone: 'warn',
      href: '/unti?linked=false',
    },
    {
      testId: 'unti-widget-declined',
      label: 'Отказов по УНТИ',
      value: typeof declined === 'number' ? formatCount(declined) : '—',
      hint: 'Наших заявлений с фактом отказа (applications.unti_declined_at) — считает api, не зеркало',
      tone: 'neutral',
    },
    syncWidget(sync),
  ];
}

/** The freshness card: the api's own `state` fold, never re-derived here — the
 *  same reading the Settings panel's «Синк УНТИ (API)» row makes. */
function syncWidget(sync: Unti2035StatusDto | undefined): UntiWidget {
  if (!sync) {
    return {
      testId: 'unti-widget-sync',
      label: 'Синк УНТИ (API)',
      value: 'нет данных',
      hint: 'Блок #23.unti2035 недоступен — свежесть зеркала неизвестна',
      tone: 'neutral',
    };
  }
  const caption = syncCaption(sync);
  return {
    testId: 'unti-widget-sync',
    label: 'Синк УНТИ (API)',
    value: caption,
    hint: syncHint(sync),
    tone: sync.state === 'OK' ? 'ok' : sync.state === 'FAIL' ? 'bad' : 'neutral',
  };
}

/** «данные на …» — the sweep clock the caption names; NEVER means never. */
function syncCaption(sync: Unti2035StatusDto): string {
  if (sync.state === 'NEVER' || sync.lastRunAt === null) return 'синк ещё не запускался';
  return `данные на ${formatDateTime(sync.lastRunAt)}`;
}

function syncHint(sync: Unti2035StatusDto): string {
  if (sync.state === 'FAIL') {
    const code = sync.errorCode ? ` (${sync.errorCode})` : '';
    return `Последний синк завершился с ошибкой${code}`;
  }
  if (sync.state === 'NEVER') return 'Синк ещё не запускался на этом стенде';
  return `Последний синк: ${sync.lastCount ?? '—'} заявлений получено`;
}

// ---------------------------------------------------------------------------
// The our-data link (the drawer's «Открыть в реестре»)
// ---------------------------------------------------------------------------

/**
 * The register link of one mirror row. The register has NO dedicated unti-id
 * parameter (`applications-model.ts` PARAM set, checked) — but #3's `q` free
 * text searches `unti_id` among its five fields (verified against
 * `applications.service.ts`), so `?q=<unti_id>` is the honest target: it lands
 * on the register showing OUR rows of exactly this child. A child may hold
 * several applications, and a mirror row with no match answers an empty
 * register — both are true readings of the same search, so the link is
 * labelled «найти в реестре» in spirit (see the drawer's button caption).
 */
export function applicationsRegisterLink(untiId: string): string {
  return `/applications?q=${encodeURIComponent(untiId)}`;
}

// ---------------------------------------------------------------------------
// The detail drawer — ALL 39 fields, grouped
// ---------------------------------------------------------------------------

/** `UntiApplicationDto` minus the api-added keys: exactly the 39 verbatim fields. */
export type UntiFieldValue = keyof Omit<UntiApplicationDto, 'id' | 'firstSeenAt' | 'lastSeenAt' | 'our'>;

export interface UntiFieldSpec {
  field: UntiFieldValue;
  /** The RU caption the drawer prints. */
  label: string;
  /** Their raw column name, printed small — «как в API» is the page's point. */
  api: string;
  /** The identity fields lead the grid full-width. */
  wide?: boolean;
}

export interface UntiFieldGroup {
  key: string;
  title: string;
  fields: ReadonlyArray<UntiFieldSpec>;
}

/**
 * The five groups of the owner brief — идентификация / статус / ЕВИ и
 * испытания / завершение / служебные — covering ALL 39 mirror fields exactly
 * once (the unit suite pins the count and the no-gap/no-overlap property).
 * Grouping follows the integration plan's own field walk
 * (`docs/UNTI-API-INTEGRATION-PLAN.md`): admission/expulsion orders sit with
 * the status block, `future_code_module_*` with the identity.
 */
export const UNTI_FIELD_GROUPS: ReadonlyArray<UntiFieldGroup> = [
  {
    key: 'identity',
    title: 'Идентификация',
    fields: [
      { field: 'applicationId', label: 'ID заявления', api: 'id', wide: true },
      { field: 'untiId', label: 'УНТИ ID', api: 'unti_id', wide: true },
      { field: 'courseId', label: 'Курс', api: 'course_id' },
      { field: 'pguId', label: 'ЕПГУ ID', api: 'pgu_id' },
      { field: 'pguDt', label: 'Дата ЕПГУ', api: 'pgu_dt' },
      { field: 'serviceOkato', label: 'ОКАТО услуги', api: 'service_okato' },
      { field: 'flowId', label: 'Поток', api: 'flow_id' },
      { field: 'flowNum', label: 'Поток №', api: 'flow_num' },
      { field: 'futureCodeModuleId', label: 'Модуль', api: 'future_code_module_id' },
      { field: 'futureCodeModuleNum', label: 'Модуль №', api: 'future_code_module_num' },
      { field: 'difficultyLevel', label: 'Уровень сложности', api: 'difficulty_level' },
    ],
  },
  {
    key: 'status',
    title: 'Статус',
    fields: [
      { field: 'status', label: 'Статус', api: 'status' },
      { field: 'transfertStatus', label: 'Статус перевода', api: 'transfert_status' },
      { field: 'appliedAt', label: 'Подано', api: 'applied_at' },
      { field: 'admissionOrderType', label: 'Приказ о зачислении — тип', api: 'admission_order_type' },
      { field: 'admissionOrderNumber', label: 'Приказ о зачислении — №', api: 'admission_order_number' },
      { field: 'admissionOrderDate', label: 'Приказ о зачислении — дата', api: 'admission_order_date' },
      { field: 'expulsionDate', label: 'Отчисление — дата', api: 'expulsion_date' },
      { field: 'expulsionOrderType', label: 'Отчисление — тип приказа', api: 'expulsion_order_type' },
      { field: 'expulsionOrderNumber', label: 'Отчисление — № приказа', api: 'expulsion_order_number' },
      { field: 'expulsionOrderDate', label: 'Отчисление — дата приказа', api: 'expulsion_order_date' },
      { field: 'educationEndDate', label: 'Окончание обучения', api: 'education_end_date' },
    ],
  },
  {
    key: 'evi',
    title: 'ЕВИ и испытания',
    fields: [
      { field: 'endDt', label: 'Окончание (ЕВИ)', api: 'end_dt' },
      { field: 'untillDt', label: 'Дата автоотклонения', api: 'untill_dt' },
      { field: 'algorithmGrade', label: 'Алгоритм — оценка', api: 'algorithm_grade' },
      { field: 'languageGrade', label: 'Язык — оценка', api: 'language_grade' },
      { field: 'motivationGrade', label: 'Мотивация — оценка', api: 'motivation_grade' },
      { field: 'isTimeout', label: 'Просрочено', api: 'is_timeout' },
      { field: 'cofExitExamStatus', label: 'Выходной экзамен — статус', api: 'cof_exit_exam_status' },
      { field: 'exitAlgorithmGrade', label: 'Выходной алгоритм — оценка', api: 'exit_algorithm_grade' },
      { field: 'exitLanguageGrade', label: 'Выходной язык — оценка', api: 'exit_language_grade' },
      { field: 'exitMotivationGrade', label: 'Выходная мотивация — оценка', api: 'exit_motivation_grade' },
      { field: 'exitEndDt', label: 'Выходной экзамен — дата', api: 'exit_end_dt' },
    ],
  },
  {
    key: 'completion',
    title: 'Завершение',
    fields: [
      { field: 'finalExamDate', label: 'Итоговый экзамен — дата', api: 'final_exam_date' },
      { field: 'finalExamOrderDate', label: 'Итоговый экзамен — дата приказа', api: 'final_exam_order_date' },
      { field: 'finalExamOrderNumber', label: 'Итоговый экзамен — № приказа', api: 'final_exam_order_number' },
      { field: 'certificateUrl', label: 'Сертификат — URL', api: 'certificate_url', wide: true },
      { field: 'certificateNumber', label: 'Сертификат — №', api: 'certificate_number' },
    ],
  },
  {
    key: 'service',
    title: 'Служебные',
    fields: [{ field: 'additionalInfo', label: 'Доп. информация', api: 'additional_info', wide: true }],
  },
];

/** Every field spec, flattened — the drawer renders groups in this order. */
export function untiFieldSpecs(): ReadonlyArray<UntiFieldSpec> {
  return UNTI_FIELD_GROUPS.flatMap((group) => group.fields);
}

/** The row value a field renders: null/'' → the absence marker, anything else
 *  VERBATIM (a datetime-ish value is formatted by the page, `formatDateTime`,
 *  with the wire string kept in the title — the mirror stays «как в API»). */
export function formatFieldValue(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return value;
}

/** True for the DRF datetimes the mirror stores verbatim (`YYYY-MM-DD…`). */
export function looksLikeDatetime(value: string): boolean {
  return Number.isFinite(Date.parse(value)) && /\d{4}-\d{2}-\d{2}/.test(value);
}

// ---------------------------------------------------------------------------
// Formatting (dependency-free twins of the dashboard's helpers — the unit
// project runs this file in plain Node, so no import from a component module)
// ---------------------------------------------------------------------------

/** ISO-8601 → `ДД.ММ.ГГГГ ЧЧ:ММ`, UTC — the register's rendering rule. */
export function formatDateTime(iso: string | null | undefined, absent = '—'): string {
  if (typeof iso !== 'string' || iso.trim() === '') return absent;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return absent;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}.${date.getUTCFullYear()} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

const NBSP = ' ';

/** `2530` → `2 NBSP 530` — the RU convention, the register's rule. */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '0';
  const digits = String(Math.max(Math.round(value), 0));
  let out = '';
  for (let i = 0; i < digits.length; i += 1) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += NBSP;
    out += digits[i];
  }
  return out;
}

/** The change history's «field: old → new» line, absence folded to «(пусто)». */
export function formatChangeValue(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '(пусто)';
  return value;
}
